import type { CognitoUser, CognitoUserAttribute, CognitoUserSession, IAuthenticationCallback } from 'amazon-cognito-identity-js';

import { cognitoErrorCode, codeToMessage } from './auth-messages';
import type { CognitoModule } from './cognito';

/** Resultado de cada passo, os mesmos estados do `AuthenticationResponse` do legado. */
export type AuthResult =
  | { type: 'success'; user: CognitoUser }
  | { type: 'failure'; user?: CognitoUser; code?: string; message: string }
  | { type: 'mfa'; user: CognitoUser }
  | { type: 'mfaSetup'; user: CognitoUser; secretCode?: string }
  | { type: 'newPassword'; user: CognitoUser }
  | { type: 'verificationEmail'; user: CognitoUser };

export interface PoolConfig {
  UserPoolId: string;
  ClientId: string;
  /** `USE_MFA === 1`: TOTP obrigatório depois do login. */
  useMfa: boolean;
}

export interface UserProfile {
  name?: string;
  email?: string;
  verified: boolean;
}

export function failureFromError(error: unknown, user?: CognitoUser): AuthResult {
  const code = cognitoErrorCode(error);
  return { type: 'failure', message: codeToMessage(code), ...(code ? { code } : {}), ...(user ? { user } : {}) };
}

// A lib tipa a lista como obrigatória, mas o Cognito a omite quando o usuário não tem MFA.
const hasMfaIn = (data: { UserMFASettingList?: string[] | undefined } | undefined) => (data?.UserMFASettingList?.length ?? 0) > 0;

/** E-mail como o legado manda ao Cognito: minúsculo e sem espaço. */
export const normalizeEmail = (email: string) => email.toLocaleLowerCase().trim();

/** Atributos do Cognito → nome e verificado, como `processUserAttributes` do legado. */
export function profileFromAttributes(attributes: readonly Pick<CognitoUserAttribute, 'getName' | 'getValue'>[] | undefined): UserProfile {
  const list = attributes ?? [];
  const name = list.find((attr) => attr.getName().endsWith('name') && !!attr.getValue())?.getValue();
  const verified = list.some((attr) => attr.getName().endsWith('_verified') && !!attr.getValue());
  const email = list.find((attr) => attr.getName() === 'email' && !!attr.getValue())?.getValue();
  return { ...(name ? { name } : {}), ...(email ? { email } : {}), verified };
}

/** URI do QR do TOTP, igual ao `mfaSecret()` do legado. */
export function totpUri(username: string, secretCode: string, mainTitle: string | null | undefined) {
  return `otpauth://totp/MFA:${username}?secret=${secretCode}&issuer=UYZY ${mainTitle ?? ''}`;
}

/** Porta de `services/identity` do legado sobre a lib já carregada. */
export function createCognitoFlows(cognito: CognitoModule, pool: PoolConfig) {
  const userPool = () => new cognito.CognitoUserPool({ UserPoolId: pool.UserPoolId, ClientId: pool.ClientId });
  const userFor = (email: string) => new cognito.CognitoUser({ Username: normalizeEmail(email), Pool: userPool() });

  const associate = (user: CognitoUser, resolve: (result: AuthResult) => void) =>
    user.associateSoftwareToken({
      associateSecretCode: (secretCode) => resolve({ type: 'mfaSetup', user, secretCode }),
      onFailure: (error) => resolve(failureFromError(error, user)),
    });

  function callbacks(user: CognitoUser, resolve: (result: AuthResult) => void): IAuthenticationCallback {
    return {
      onSuccess: () => {
        if (!pool.useMfa) {
          resolve({ type: 'success', user });
          return;
        }
        user.getUserData((error, data) => {
          if (error) {
            resolve(failureFromError(error));
            return;
          }
          // Sem TOTP configurado: associa e pede o setup antes de liberar (USE_MFA).
          if (!hasMfaIn(data)) associate(user, resolve);
          else resolve({ type: 'success', user });
        });
      },
      onFailure: (error: unknown) => resolve(failureFromError(error, user)),
      newPasswordRequired: () => resolve({ type: 'newPassword', user }),
      mfaRequired: () => resolve({ type: 'mfa', user }),
      totpRequired: () => resolve({ type: 'mfa', user }),
      // Desafio MFA_SETUP do pool: o legado não pedia o segredo aqui e mostrava o QR vazio.
      mfaSetup: () => associate(user, resolve),
    };
  }

  function withSession(user: CognitoUser, run: (session: CognitoUserSession) => void, resolve: (result: AuthResult) => void) {
    user.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error || !session) {
        resolve(failureFromError(error, user));
        return;
      }
      user.setSignInUserSession(session);
      run(session);
    });
  }

  return {
    currentUser: (): CognitoUser | null => userPool().getCurrentUser(),

    signIn(email: string, password: string): Promise<AuthResult> {
      return new Promise((resolve) => {
        const username = normalizeEmail(email);
        const user = userFor(username);
        user.authenticateUser(new cognito.AuthenticationDetails({ Username: username, Password: password }), callbacks(user, resolve));
      });
    },

    confirmMfa(user: CognitoUser, code: string): Promise<AuthResult> {
      return new Promise((resolve) => user.sendMFACode(code, callbacks(user, resolve), 'SOFTWARE_TOKEN_MFA'));
    },

    confirmMfaSetup(user: CognitoUser, code: string): Promise<AuthResult> {
      return new Promise((resolve) => {
        // eslint-disable-next-line lingui/no-unlocalized-strings -- friendlyName do dispositivo, igual ao legado
        user.verifySoftwareToken(code, 'SoftwareToken', {
          onSuccess: () => {
            user.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, (error) => {
              resolve(error ? failureFromError(error, user) : { type: 'success', user });
            });
          },
          onFailure: (error) => resolve(failureFromError(error, user)),
        });
      });
    },

    /** Nova senha obrigatória: completa o desafio com `{ email }` e autentica de novo com a senha nova. */
    completeNewPassword(user: CognitoUser, email: string, newPassword: string): Promise<AuthResult> {
      return new Promise((resolve) => {
        const base = callbacks(user, resolve);
        user.completeNewPasswordChallenge(
          newPassword,
          { email },
          {
            ...base,
            onSuccess: (session) => {
              user.setSignInUserSession(session);
              user.authenticateUser(new cognito.AuthenticationDetails({ Username: email, Password: newPassword }), base);
            },
          },
        );
      });
    },

    sendEmailVerificationCode(user: CognitoUser): Promise<AuthResult> {
      return new Promise((resolve) => {
        withSession(
          user,
          () =>
            user.getAttributeVerificationCode('email', {
              onSuccess: () => resolve({ type: 'verificationEmail', user }),
              onFailure: (error) => resolve(failureFromError(error, user)),
              inputVerificationCode: () => resolve({ type: 'verificationEmail', user }),
            }),
          resolve,
        );
      });
    },

    confirmEmailCode(user: CognitoUser, code: string): Promise<AuthResult> {
      return new Promise((resolve) => {
        withSession(
          user,
          () =>
            user.verifyAttribute('email', code.trim(), {
              onSuccess: () => resolve({ type: 'success', user }),
              onFailure: (error) => resolve(failureFromError(error, user)),
            }),
          resolve,
        );
      });
    },

    /** Troca de senha logado. O legado passava a função no lugar do erro e sempre caía em "Erro inesperado". */
    changePassword(user: CognitoUser, oldPassword: string, newPassword: string): Promise<AuthResult> {
      return new Promise((resolve) => {
        user.setAuthenticationFlowType('REFRESH_TOKEN');
        withSession(
          user,
          () =>
            user.changePassword(oldPassword, newPassword, (error) => {
              resolve(error ? failureFromError(error, user) : { type: 'success', user });
            }),
          resolve,
        );
      });
    },

    sendPasswordRecoveryCode(email: string): Promise<AuthResult> {
      return new Promise((resolve) => {
        const user = userFor(email);
        user.forgotPassword({
          onSuccess: () => resolve({ type: 'verificationEmail', user }),
          onFailure: (error) => resolve(failureFromError(error, user)),
          inputVerificationCode: () => resolve({ type: 'verificationEmail', user }),
        });
      });
    },

    confirmPasswordRecovery(email: string, code: string, password: string): Promise<AuthResult> {
      return new Promise((resolve) => {
        const user = userFor(email);
        user.confirmPassword(code, password, {
          onSuccess: () => resolve({ type: 'success', user }),
          onFailure: (error) => resolve(failureFromError(error, user)),
        });
      });
    },

    getSession(user: CognitoUser): Promise<CognitoUserSession | null> {
      return new Promise((resolve) => {
        user.getSession((error: Error | null, session: CognitoUserSession | null) => {
          resolve(error || !session?.isValid() ? null : session);
        });
      });
    },

    getProfile(user: CognitoUser): Promise<UserProfile | null> {
      return new Promise((resolve) => {
        user.getUserAttributes((error, attributes) => resolve(error ? null : profileFromAttributes(attributes)));
      });
    },

    hasMfaConfigured(user: CognitoUser): Promise<boolean | null> {
      return new Promise((resolve) => {
        user.getUserData((error, data) => resolve(error ? null : hasMfaIn(data)));
      });
    },

    associateSoftwareToken(user: CognitoUser): Promise<AuthResult> {
      return new Promise((resolve) => associate(user, resolve));
    },
  };
}

export type CognitoFlows = ReturnType<typeof createCognitoFlows>;
